'use client';

import { type ReactNode, type TouchEvent, useEffect, useRef, useState } from 'react';
import { cn } from '@/app/utils';

interface CarouselProps {
  items: ReactNode[];
  className?: string;
  slideClassName?: string;
  autoPlay?: boolean;
  interval?: number;
  indicatorActiveColor?: string;
  indicatorColor?: string;
  onIndexChange?: (idx: number) => void;
  indicatorPosition?: 'outside' | 'none' | 'inside';
  enableSwipe?: boolean;
  indicatorClassName?: string;
}

export function Carousel({
  items,
  className,
  slideClassName,
  autoPlay = false,
  interval = 3000,
  indicatorColor,
  indicatorActiveColor,
  onIndexChange,
  indicatorPosition = 'outside',
  enableSwipe = true,
  indicatorClassName,
}: CarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(1); // 从1开始，因为第一个是克隆的最后一个
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const minSwipeDistance = 50;
  const containerRef = useRef<HTMLDivElement>(null);

  // 创建循环数组：[last, ...items, first]
  const extendedItems = [items[items.length - 1], ...items, items[0]];

  useEffect(() => {
    if (!autoPlay) return;

    const timer = setInterval(() => {
      goToSlide(currentIndex + 1);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, currentIndex]);

  const handleTransitionEnd = () => {
    if (!isTransitioning) return;
    setIsTransitioning(false);

    // 处理循环逻辑
    if (currentIndex >= items.length + 1) {
      setCurrentIndex(1); // 跳回真实的第一张
      onIndexChange?.(1);
    } else if (currentIndex <= 0) {
      setCurrentIndex(items.length); // 跳回真实的最后一张
      onIndexChange?.(items.length);
    }
  };

  const goToSlide = (index: number) => {
    setIsTransitioning(true);
    setCurrentIndex(index);
    onIndexChange?.(index);
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (!enableSwipe) {
      return;
    }
    setTouchStart(e.touches[0].clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!enableSwipe) {
      return;
    }
    if (!isDragging) return;
    const currentTouch = e.touches[0].clientX;
    const diff = currentTouch - touchStart;
    setDragOffset(diff);
    setTouchEnd(currentTouch);
  };

  const handleTouchEnd = () => {
    if (!enableSwipe) {
      return;
    }
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToSlide(currentIndex + 1);
    } else if (isRightSwipe) {
      goToSlide(currentIndex - 1);
    }

    setTouchStart(0);
    setTouchEnd(0);
    setDragOffset(0);
  };

  // 计算实际显示的索引（用于指示器）
  const actualIndex = currentIndex - 1;

  return (
    <div className={cn('relative', className)}>
      <div className="relative flex h-full flex-col overflow-hidden" ref={containerRef}>
        <div
          className={cn(
            'flex flex-1 touch-pan-y',
            isTransitioning && !isDragging ? 'transition-transform duration-300 ease-in-out' : '',
          )}
          style={{
            transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))`,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTransitionEnd={handleTransitionEnd}
        >
          {extendedItems.map((item, index) => (
            <div key={`carousel-item-${index + 1}`} className={cn('box-border min-w-full', slideClassName)}>
              {item}
            </div>
          ))}
        </div>

        {/* Dots */}
        {indicatorPosition !== 'none' && (
          <div className={cn('mt-6 flex flex-shrink-0 justify-center gap-2', indicatorClassName)}>
            {items.map((_, index) => (
              <button
                key={`carousel-indicator-${index + 1}`}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-all',
                  index === actualIndex % items.length
                    ? `w-4 ${indicatorActiveColor || 'bg-black'}`
                    : `${indicatorColor || 'bg-gray-300'}`,
                )}
                onClick={() => goToSlide(index + 1)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
